/**
 * Right-rail "Voice" tab: microphone setup, the recorder card and the list of
 * every voice note recorded on this case (all authors), with playback.
 *
 * Shown to every user with case access; recording is offered only when
 * `canRecord` (own in-progress submission, not review / view-only).
 */
import { Button } from "@frontend/components/ui/button"
import { Input } from "@frontend/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { Slider } from "@frontend/components/ui/slider"
import { Switch } from "@frontend/components/ui/switch"
import { Textarea } from "@frontend/components/ui/textarea"
import { formatElapsedTime } from "@frontend/pages/case-workspace/lib/elapsed-time"
import {
  type AudioInputDevicesState,
  useAudioLevelMeter
} from "@frontend/pages/case-workspace/pathology/hooks/use-audio-input-devices"
import type { VoiceNotePlaybackState } from "@frontend/pages/case-workspace/pathology/hooks/use-voice-note-playback"
import type {
  VoiceNoteListItem,
  VoiceNoteRecorderState
} from "@frontend/pages/case-workspace/pathology/hooks/use-voice-note-recorder"
import { Download, Loader2, Mic, MicOff, Pause, Play, RotateCcw, Square, Trash2, X } from "lucide-react"
import { useMemo } from "react"

const SYSTEM_DEFAULT_VALUE = "__system_default__"

interface VoiceNotesPanelProps {
  notes: VoiceNoteListItem[] | undefined
  isLoadingNotes: boolean
  canRecord: boolean
  /** Why recording is not offered (review mode, view only, ...); null when it is. */
  recordDisabledReason: string | null
  ownSubmissionId: string | null
  recorder: VoiceNoteRecorderState
  playback: VoiceNotePlaybackState
  audioInputs: AudioInputDevicesState
  activeSeriesId: string | null
  /** Base series id → display label, for the slide name on each note. */
  sourceNameById: Map<string, string>
  onDelete: (note: VoiceNoteListItem) => void
}

interface NoteGroup {
  key: string
  title: string
  own: boolean
  notes: VoiceNoteListItem[]
}

function groupNotes(notes: VoiceNoteListItem[], ownSubmissionId: string | null): NoteGroup[] {
  const groups = new Map<string, NoteGroup>()
  for (const note of notes) {
    const own = note.submission_id === ownSubmissionId
    const key = own ? "__own__" : note.submission_user_id
    let group = groups.get(key)
    if (!group) {
      group = {
        key,
        title: own ? "My notes" : note.submission_user_name?.trim() || "Another annotator",
        own,
        notes: []
      }
      groups.set(key, group)
    }
    group.notes.push(note)
  }
  return [...groups.values()].sort((a, b) => Number(b.own) - Number(a.own) || a.title.localeCompare(b.title))
}

function formatDuration(ms: number): string {
  return formatElapsedTime(Math.round(ms / 1000)).replace(/^00:/, "")
}

function formatRelative(iso: string | null): string {
  if (!iso) return ""
  const diffMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return new Date(iso).toLocaleDateString()
}

function LevelMeter({ stream }: { stream: MediaStream | null }) {
  const level = useAudioLevelMeter(stream)
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden" aria-label="Microphone input level">
      <div
        className={`h-full rounded-full transition-[width] duration-75 ${level > 0.85 ? "bg-red-500" : "bg-green-500"}`}
        style={{ width: `${Math.round(level * 100)}%` }}
      />
    </div>
  )
}

function MicrophoneSetup({ audioInputs, disabled }: { audioInputs: AudioInputDevicesState; disabled: boolean }) {
  const { devices, selectedDeviceId, setSelectedDeviceId, permissionState, requestAccess, previewStream } = audioInputs
  const hasDevices = devices.length > 0

  if (permissionState === "denied") {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-[11px] text-destructive flex gap-1.5">
        <MicOff className="size-3.5 shrink-0 mt-px" />
        <span>Microphone blocked. Allow it in the browser's site settings for this page, then reload.</span>
      </div>
    )
  }

  if (!hasDevices) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-[11px] w-full"
        disabled={disabled}
        onClick={() => void requestAccess()}
      >
        <Mic className="size-3.5 mr-1.5" />
        Set up microphone
      </Button>
    )
  }

  const selectedLabel = devices.find((d) => d.deviceId === selectedDeviceId)?.label ?? "System default"
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Select
          value={selectedDeviceId ?? SYSTEM_DEFAULT_VALUE}
          onValueChange={(value) => setSelectedDeviceId(value === SYSTEM_DEFAULT_VALUE || !value ? null : value)}
          disabled={disabled}
        >
          <SelectTrigger size="sm" className="flex-1 min-w-0 w-full" aria-label="Microphone">
            <Mic className="size-3 shrink-0 text-muted-foreground" />
            <SelectValue className="truncate">{selectedLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent className="w-auto min-w-[var(--anchor-width)] max-w-[90vw]">
            <SelectItem value={SYSTEM_DEFAULT_VALUE}>System default</SelectItem>
            {devices.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                {device.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={previewStream ? "default" : "outline"}
          size="sm"
          className="h-6 px-2 text-[10px] shrink-0"
          disabled={disabled}
          title="Open the microphone for a few seconds to check its level. Bluetooth headsets switch to a lower-quality hands-free profile when used as input."
          onClick={() => (previewStream ? audioInputs.stopPreview() : void audioInputs.startPreview())}
        >
          {previewStream ? "Stop test" : "Test"}
        </Button>
      </div>
    </div>
  )
}

function RecorderCard({
  recorder,
  audioInputs,
  canRecord
}: {
  recorder: VoiceNoteRecorderState
  audioInputs: AudioInputDevicesState
  canRecord: boolean
}) {
  const { status } = recorder
  const busy = status === "requesting" || status === "uploading"
  const live = status === "recording" || status === "paused"
  const meterStream = recorder.stream ?? audioInputs.previewStream

  if (recorder.unsupportedReason) {
    return (
      <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground flex gap-1.5">
        <MicOff className="size-3.5 shrink-0 mt-px" />
        <span>{recorder.unsupportedReason}</span>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-border bg-card p-2.5 space-y-2">
      <MicrophoneSetup audioInputs={audioInputs} disabled={live || busy} />
      {(meterStream || live) && <LevelMeter stream={meterStream} />}

      <div className="flex items-center gap-2">
        {status === "idle" && (
          <Button
            size="sm"
            className="h-7 text-[11px] flex-1"
            disabled={!canRecord || audioInputs.permissionState === "denied"}
            onClick={() => void recorder.start()}
            title="Record a voice note (V)"
          >
            <span className="size-2 rounded-full bg-red-500 mr-1.5" />
            Record
          </Button>
        )}
        {status === "requesting" && (
          <Button size="sm" className="h-7 text-[11px] flex-1" disabled>
            <Loader2 className="size-3.5 mr-1.5 animate-spin" />
            Starting…
          </Button>
        )}
        {live && (
          <>
            <span className="flex items-center gap-1.5 font-mono tabular-nums text-[12px]">
              <span
                className={`size-2 rounded-full ${status === "recording" ? "bg-red-500 animate-pulse" : "bg-amber-500"}`}
              />
              {formatElapsedTime(Math.floor(recorder.elapsedMs / 1000))}
            </span>
            <div className="ml-auto flex items-center gap-1">
              {status === "recording" ? (
                <Button variant="outline" size="icon" className="size-7" title="Pause" onClick={recorder.pause}>
                  <Pause className="size-3.5" />
                </Button>
              ) : (
                <Button variant="outline" size="icon" className="size-7" title="Resume" onClick={recorder.resume}>
                  <Play className="size-3.5" />
                </Button>
              )}
              <Button size="icon" className="size-7" title="Stop and save (V)" onClick={recorder.stop}>
                <Square className="size-3.5" />
              </Button>
            </div>
          </>
        )}
        {status === "uploading" && (
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Saving voice note…
          </span>
        )}
        {status === "upload-failed" && (
          <div className="flex items-center gap-1.5 w-full">
            <span className="text-[11px] text-destructive flex-1">
              Upload failed. The recording is still in memory.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[10px]"
              onClick={() => void recorder.retryUpload()}
            >
              <RotateCcw className="size-3 mr-1" />
              Retry
            </Button>
            <Button variant="ghost" size="icon" className="size-6" title="Discard recording" onClick={recorder.discard}>
              <X className="size-3" />
            </Button>
          </div>
        )}
      </div>

      {(status === "idle" || live) && (
        <div className="space-y-1.5">
          <Input
            value={recorder.title}
            onChange={(event) => recorder.setTitle(event.target.value)}
            placeholder="Title (optional)"
            className="h-7 text-[11px]"
            maxLength={120}
            disabled={busy}
          />
          <Textarea
            value={recorder.caption}
            onChange={(event) => recorder.setCaption(event.target.value)}
            placeholder="Short caption (optional)"
            className="min-h-[44px] text-[11px]"
            maxLength={500}
            disabled={busy}
          />
          <p className="text-[10px] text-muted-foreground">
            Press <kbd className="rounded border border-border px-1">V</kbd> in the viewer to start / stop. The cursor
            and view are recorded with the audio.
          </p>
        </div>
      )}
    </div>
  )
}

function NoteRow({
  note,
  own,
  playback,
  activeSeriesId,
  sourceName,
  onDelete
}: {
  note: VoiceNoteListItem
  own: boolean
  playback: VoiceNotePlaybackState
  activeSeriesId: string | null
  sourceName: string | null
  onDelete: () => void
}) {
  const isActive = playback.activeNote?.id === note.id
  const isPlaying = isActive && playback.isPlaying
  const isLoading = isActive && playback.isLoading
  const onOtherSlide = Boolean(note.series_id) && note.series_id !== activeSeriesId
  const durationMs = Math.max(1, note.duration_ms)
  const positionMs = isActive ? Math.min(playback.currentMs, durationMs) : 0

  return (
    <li
      className={`rounded-md border px-2.5 py-2 space-y-1.5 ${isActive ? "border-primary/50 bg-primary/5" : "border-border"}`}
    >
      <div className="flex items-start gap-2">
        <Button
          variant={isActive ? "default" : "outline"}
          size="icon"
          className="size-7 shrink-0"
          title={isPlaying ? "Pause" : "Play"}
          disabled={!note.file_url}
          onClick={() => {
            if (isPlaying) playback.pause()
            else if (isActive) playback.resume()
            else void playback.play(note)
          }}
        >
          {isLoading ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="size-3.5" />
          ) : (
            <Play className="size-3.5" />
          )}
        </Button>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium truncate">{note.title?.trim() || "Untitled note"}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            {formatDuration(note.duration_ms)} · {formatRelative(note.created_at)}
            {sourceName ? ` · ${sourceName}` : ""}
          </div>
          {note.caption && (
            <div className="text-[10px] text-muted-foreground/90 mt-0.5 line-clamp-3">{note.caption}</div>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            title="Download CBOR file"
            disabled={!note.file_url}
            onClick={() => void playback.download(note)}
          >
            <Download className="size-3" />
          </Button>
          {own && (
            <Button
              variant="ghost"
              size="icon"
              className="size-6 text-destructive"
              title="Delete note"
              onClick={onDelete}
            >
              <Trash2 className="size-3" />
            </Button>
          )}
        </div>
      </div>

      {isActive && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="font-mono tabular-nums text-[10px] text-muted-foreground w-10 shrink-0">
              {formatDuration(positionMs)}
            </span>
            <Slider
              className="flex-1"
              min={0}
              max={durationMs}
              value={[positionMs]}
              onValueChange={(value) => {
                const vals = Array.isArray(value) ? value : [value]
                playback.seek(vals[0] ?? 0)
              }}
              aria-label="Playback position"
            />
            <Button variant="ghost" size="icon" className="size-6" title="Stop" onClick={playback.stop}>
              <Square className="size-3" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
            <label className="flex items-center gap-1.5">
              <Switch
                size="sm"
                checked={playback.follow}
                onCheckedChange={(checked) => playback.setFollow(checked)}
                disabled={onOtherSlide}
                aria-label="Follow the recorded camera"
              />
              Follow camera
            </label>
            {onOtherSlide && <span className="text-amber-500">Recorded on another slide</span>}
          </div>
        </div>
      )}
    </li>
  )
}

export function VoiceNotesPanel({
  notes,
  isLoadingNotes,
  canRecord,
  recordDisabledReason,
  ownSubmissionId,
  recorder,
  playback,
  audioInputs,
  activeSeriesId,
  sourceNameById,
  onDelete
}: VoiceNotesPanelProps) {
  const groups = useMemo(() => groupNotes(notes ?? [], ownSubmissionId), [notes, ownSubmissionId])

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="px-3 py-2 border-b border-border shrink-0">
        <div className="text-[11px] font-semibold">Voice notes</div>
        <div className="text-[10px] text-muted-foreground">Spoken comments replayed with the cursor and view</div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
        {canRecord ? (
          <RecorderCard recorder={recorder} audioInputs={audioInputs} canRecord={canRecord} />
        ) : (
          recordDisabledReason && (
            <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2 text-[10px] text-muted-foreground">
              {recordDisabledReason}
            </div>
          )
        )}

        {isLoadingNotes && !notes ? (
          <div className="text-[11px] text-muted-foreground">Loading notes…</div>
        ) : groups.length === 0 ? (
          <div className="text-[11px] text-muted-foreground">No voice notes on this case yet.</div>
        ) : (
          groups.map((group) => (
            <section key={group.key} className="space-y-1.5">
              <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {group.title} <span className="normal-case tracking-normal">({group.notes.length})</span>
              </h3>
              <ul className="space-y-1.5">
                {group.notes.map((note) => (
                  <NoteRow
                    key={note.id}
                    note={note}
                    own={group.own}
                    playback={playback}
                    activeSeriesId={activeSeriesId}
                    sourceName={note.series_id ? (sourceNameById.get(note.series_id) ?? null) : null}
                    onDelete={() => onDelete(note)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  )
}
